/**
 * Shadow DOM Style Management
 *
 * Centralized utility for managing stylesheet loading and injection into shadow DOM.
 * This ensures styles are loaded only once, parsed once, and properly encapsulated within shadow DOM.
 * Uses the modern Constructable StyleSheets API for better performance.
 */

// Define a type for window with our global state
interface FluidWindow extends Window {
  __FLUID_STYLE_TEXT?: string;
  __FLUID_STYLE_URL?: string;
  __FLUID_SHADOW_STYLESHEET?: CSSStyleSheet;
  __FLUID_SHADOW_STYLESHEET_LOADING?: boolean;
}

// Check for browser environment before accessing window
const isServer = typeof window === "undefined";

/**
 * Apply shared styles to a shadow root
 * Uses Constructable StyleSheets API where available, falls back to traditional methods
 */
export function applyStylesIntoShadowRoot(shadowRoot: ShadowRoot): void {
  // Skip in SSR environment
  if (isServer) return;

  // Check if browser supports Constructable StyleSheets
  const supportsConstructableStyleSheets =
    "adoptedStyleSheets" in Document.prototype &&
    "adoptedStyleSheets" in ShadowRoot.prototype;

  if (supportsConstructableStyleSheets) {
    // Modern approach: Use Constructable StyleSheets API
    void applyConstructableStyleSheet(shadowRoot);
  } else {
    // Legacy approach: Use style/link elements
    applyLegacyStyles(shadowRoot);
  }
}

/**
 * Apply styles using modern Constructable StyleSheets API
 */
async function applyConstructableStyleSheet(
  shadowRoot: ShadowRoot,
): Promise<void> {
  if (isServer) return;

  try {
    // Get or create stylesheet
    const stylesheet = await getOrCreateStyleSheet();

    if (stylesheet) {
      // Apply to shadow root if not already present
      const existingSheets = Array.from(shadowRoot.adoptedStyleSheets);
      if (!existingSheets.includes(stylesheet)) {
        shadowRoot.adoptedStyleSheets = [...existingSheets, stylesheet];
      }
    } else {
      // Fall back to legacy approach if we couldn't get a stylesheet
      applyLegacyStyles(shadowRoot);
    }
  } catch (error) {
    console.error("Error applying constructable stylesheet:", error);
    // Fall back to legacy approach
    applyLegacyStyles(shadowRoot);
  }
}

/**
 * Get an existing stylesheet or create a new one
 */
async function getOrCreateStyleSheet(): Promise<CSSStyleSheet | null> {
  if (isServer) return null;

  const fluidWindow = window as unknown as FluidWindow;

  // Return cached stylesheet if available
  if (fluidWindow.__FLUID_SHADOW_STYLESHEET) {
    return fluidWindow.__FLUID_SHADOW_STYLESHEET;
  }

  // Return null if already loading to avoid duplicate loads
  if (fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING) {
    return null;
  }

  // Mark as loading
  fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = true;

  try {
    // Create a new stylesheet
    const stylesheet = new CSSStyleSheet();

    // Get CSS text from cache or fetch
    const cssText = await getCssText();

    if (cssText) {
      // Replace the contents with our CSS
      await stylesheet.replace(cssText);

      // Cache the stylesheet
      fluidWindow.__FLUID_SHADOW_STYLESHEET = stylesheet;

      return stylesheet;
    }

    return null;
  } catch (error) {
    console.error("Error creating stylesheet:", error);
    return null;
  } finally {
    // Reset loading flag
    fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = false;
  }
}

/**
 * Get CSS text from cache or fetch it
 */
async function getCssText(): Promise<string | null> {
  if (isServer) return null;

  const fluidWindow = window as unknown as FluidWindow;

  // Use cached CSS text if available
  if (fluidWindow.__FLUID_STYLE_TEXT) {
    return fluidWindow.__FLUID_STYLE_TEXT;
  }

  // Try to fetch CSS if we have a URL
  if (fluidWindow.__FLUID_STYLE_URL) {
    try {
      const response = await fetch(fluidWindow.__FLUID_STYLE_URL);
      if (response.ok) {
        const cssText = await response.text();
        // Cache the CSS text
        fluidWindow.__FLUID_STYLE_TEXT = cssText;
        return cssText;
      }
    } catch (error) {
      console.error("Error fetching CSS:", error);
    }
  }

  return null;
}

/**
 * Legacy fallback for browsers that don't support adoptedStyleSheets
 */
function applyLegacyStyles(shadowRoot: ShadowRoot): void {
  if (isServer) return;

  const fluidWindow = window as unknown as FluidWindow;

  // Check if styles are already injected
  if (shadowRoot.querySelector('style[data-fluid-injected="true"]')) {
    return;
  }

  // If we have the CSS text cached, use it directly
  if (fluidWindow.__FLUID_STYLE_TEXT) {
    const styleElement = document.createElement("style");
    styleElement.textContent = fluidWindow.__FLUID_STYLE_TEXT;
    styleElement.setAttribute("data-fluid-injected", "true");
    shadowRoot.appendChild(styleElement);
  } else if (fluidWindow.__FLUID_STYLE_URL) {
    // If we don't have CSS text, try to use a link element with the URL
    const linkElement = document.createElement("link");
    linkElement.rel = "stylesheet";
    linkElement.href = fluidWindow.__FLUID_STYLE_URL;
    linkElement.setAttribute("data-fluid-injected", "true");
    shadowRoot.appendChild(linkElement);
  }
}
